import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SupplierService } from '../shared/supplier.service';
import { Save, X, CheckCircle } from 'lucide-angular';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { SupplierModel } from '../shared/supplier.model';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { SelectComponent } from '@app/partials/form/select/select.component';
import { AbstractEntityEditComponent } from '@app/shared/abstract/abstract-entity-edit.component';
import { ServiceService } from '../shared/service.service';
import { RouteParams } from '@app/shared/abstract/route-params';
import { map, Observable } from 'rxjs';
import { ServiceModel } from '../shared/service.model';

@Component({
  selector: 'app-edit',
  imports: [ButtonComponent, FormsModule, ReactiveFormsModule, TextInputComponent, SelectComponent],
  templateUrl: './supplier-edit.component.html',
  styleUrl: './supplier-edit.component.css',
})
export class SupplierEditComponent extends AbstractEntityEditComponent {
  protected processing = false;
  protected validationErrors: string[] = [];
  protected supplier: SupplierModel;
  protected readonly SaveIcon = Save;
  protected readonly XIcon = X;
  protected readonly CheckCircleIcon = CheckCircle;

  statusChoices = [
    { value: 'Active', label: 'Active' },
    { value: 'Inactive', label: 'Inactive' },
  ];

  imageUrl: string | ArrayBuffer | null = null;
  imageFile: File | null = null;

  form = new FormGroup({
    name: new FormControl('', [Validators.required, Validators.maxLength(255)]),
    description: new FormControl('', Validators.required),
    status: new FormControl('', [Validators.required, Validators.pattern(/^(Active|Inactive)$/)]),
    phone: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.required, Validators.email]),
    services: new FormControl<number[]>([], Validators.required),
    website: new FormControl('', [
      Validators.required,
      Validators.pattern('^(https?:\\/\\/)?([\\da-z.-]+)\\.([a-z.]{2,6})([\\/\\w .-]*)*\\/?$'),
    ]),
  });

  protected serviceChoices: Observable<{ value: any; label: string }[]>;

  constructor(
    private supplierService: SupplierService,
    private serviceService: ServiceService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    super();
    this.form.valueChanges.subscribe((value) => {
      if (this.form.dirty && !this.form.valid) {
        this.validationErrors = this.getFormValidationErrors(this.form);
      }
    });

    this.serviceChoices = this.serviceService
      .getPage(new RouteParams({ pageSize: 1000, pageNum: 1 }))
      .pipe(map((res) => res.data.map((item) => ({ value: item.id, label: item.name }))));

    if (this.route.snapshot.data['supplier']) {
      this.supplier = this.route.snapshot.data['supplier'];
      this.form.patchValue(this.supplier as any);
      this.form.controls['services'].setValue(this.supplier.services.map((service) => service.id as number));
    } else {
      this.supplier = {} as SupplierModel;
      this.supplier.images = [];
      this.supplier.status = 'Active';
      this.form.patchValue(this.supplier as any);
    }
  }

  save() {
    this.performSave(false);
  }

  saveAndContinue() {
    this.performSave(true);
  }

  private performSave(continueEditing: boolean) {
    if (this.form.invalid) {
      return;
    }
    this.processing = true;

    const formData = new FormData();
    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);
      if (control) {
        formData.append(key, control.value);
      }
    });
    if (this.imageFile) {
      formData.append('image', this.imageFile);
    }

    this.supplierService.saveFormData(formData, this.supplier?.id).subscribe({
      next: (res) => {
        this.processing = false;
        this.supplier = res.data;
        this.form.patchValue(this.supplier as any);
        this.form.controls['services'].setValue(this.supplier.services.map((service) => service.id as number));
        this.form.markAsPristine();

        if (continueEditing) {
          // For saveAndContinue, stay on edit page
          if (!this.supplier.id && res.data) {
            this.router.navigate(['/suppliers/' + res.data.id + '/edit']);
          }
          // If editing existing supplier, just stay on current page (no navigation)
        } else {
          // For regular save, go to index page
          this.router.navigate(['/suppliers']);
        }
      },
      error: (res) => {
        if (res.status === 422) {
          AbstractEntityEditComponent.bindErrorsToForm(res.error, this.form);
        }
        this.processing = false;
      },
    });
  }

  cancel() {
    return this.router.navigate(['/suppliers']);
  }

  onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length) {
      const file = input.files[0];
      const reader = new FileReader();
      this.imageFile = file;

      reader.onload = () => {
        this.imageUrl = reader.result;
        this.form.markAsDirty();
      };

      reader.readAsDataURL(file);
    }
  }

  createService(service: { name: string }) {
    this.serviceService.save(service as ServiceModel).subscribe((res) => {
      this.serviceChoices = this.serviceService
        .getPage(new RouteParams({ pageSize: 1000, pageNum: 1 }))
        .pipe(map((res) => res.data.map((item) => ({ value: item.id, label: item.name }))));
    });
  }

  protected getForms(): FormGroup<any>[] {
    return [this.form];
  }
}
